import { zeroAddress } from 'viem'
import { exactlyConfig, exactlyMarketsByChain } from '@1delta/data-sdk'
import { ExactlyPreviewerAbi } from '@1delta/abis'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'
import { EXACTLY_LENDER_KEY } from '../../../lending/public-data/exactly/convertPublic'

/**
 * Exactly oracle price fetcher.
 *
 * Exactly is a fixed-rate/fixed-maturity POOL protocol (Optimism + Base). Its
 * `Previewer.exactly(account)` view returns every market with a `usdPrice`
 * field — a DIRECT USD price (1e18-scaled) sourced from the market's own
 * Chainlink price feed (the same oracle Exactly liquidates against). So unlike
 * Morpho/Midnight (whose oracles return collateral priced in the loan asset and
 * need a second-pass USD derivation), Exactly prices are direct USD and belong
 * in PASS 1 — they feed the shared `usdPrices` map for every Exactly asset.
 *
 * Why a dedicated fetcher (not cross-protocol flattening): Exactly lists assets
 * no other protocol on its chains prices (e.g. cbXRP on Base), so without this
 * their markets would have no oracle price. It also uses Exactly's own feed for
 * the assets other protocols DO cover, which is the correct oracle for risk.
 *
 * One `Previewer.exactly(address(0))` call per chain returns the whole set, so
 * the fetcher emits a single call and the parser fans the result out to one
 * entry per market asset, all under the single per-chain `EXACTLY` key (Exactly
 * is cross-margin — the asset segment of the marketUid is the discriminator).
 */

/** Grouped metadata: the previewer answering the single aggregate call. */
interface ExactlyChainMeta {
  previewer: string
}

/** Build the (single) multicall call for Exactly oracle prices. */
export function getExactlyCalls(
  chainId: string,
): FetcherResult<ExactlyChainMeta>[] {
  const previewer = exactlyConfig()?.[chainId]?.previewer
  // Guard on a configured market set too, so chains with only a stale config
  // entry don't issue a pointless previewer call.
  if (!previewer || exactlyMarketsByChain(chainId).length === 0) return []

  const calls: MulticallCall[] = [
    { address: previewer, name: 'exactly', params: [zeroAddress] },
  ]
  return [{ calls, meta: { previewer }, lender: 'EXACTLY' }]
}

/**
 * Parse the aggregate `Previewer.exactly` result into direct-USD price entries.
 *
 * `data[0]` is the decoded `MarketAccount[]`. Each carries `market`, `asset`
 * and `usdPrice` (1e18-scaled USD). One entry per market asset, priced in USD,
 * keyed under the single `EXACTLY` lender so the marketUids join the Exactly
 * lending data emitted by `convertExactlyMarketsToResponse`.
 */
export function parseExactlyResults(
  data: any[],
  _meta: ExactlyChainMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const raw = data?.[0]
  if (!raw || raw === '0x' || !Array.isArray(raw)) return []

  // The Previewer returns EVERY on-chain Exactly market; scope emission to the
  // markets we track (config), mirroring the Midnight fetcher — untracked
  // markets' price entries would never join any lending row. Empty set ⇒ price
  // all returned markets (defensive: never drop everything on a config miss).
  const tracked = new Set(
    exactlyMarketsByChain(chainId).map((m) => m.market.toLowerCase()),
  )

  const entries: OraclePriceEntry[] = []
  for (const m of raw as any[]) {
    const asset = String(m?.asset ?? '').toLowerCase()
    const market = String(m?.market ?? '')
    const usdPrice = m?.usdPrice
    if (!asset || !market || usdPrice == null) continue
    if (tracked.size > 0 && !tracked.has(market.toLowerCase())) continue
    const priceUSD = Number(usdPrice) / 1e18
    if (!(priceUSD > 0)) continue

    entries.push({
      asset,
      // Direct USD price — no oracle-unit denomination, so raw == USD.
      price: priceUSD,
      priceUSD,
      // ONE Exactly key per chain (cross-margin); the asset segment of the
      // marketUid is what distinguishes the per-asset rows.
      marketUid: createMarketUid(chainId, EXACTLY_LENDER_KEY, asset),
      targetLender: EXACTLY_LENDER_KEY,
      description: 'Exactly market oracle (USD)',
    })
  }
  return entries
}

/** ABI for the Exactly oracle call (the Previewer aggregate view). */
export function getExactlyAbi() {
  return ExactlyPreviewerAbi
}

/** Exactly Oracle Fetcher module. */
export const exactlyFetcher = {
  getCalls: getExactlyCalls,
  parse: parseExactlyResults,
  getAbi: getExactlyAbi,
}
